import type { UserRole } from '@vayu-saathi/shared-types';

// SUPER_ADMIN is the company owner in single-company Vayu Saathi.
export const ROLE_LABELS: Record<UserRole, string> = {
  SUPER_ADMIN: 'Owner',
  ORG_ADMIN: 'Org Admin',
  HR: 'HR',
  MANAGER: 'Manager',
  EMPLOYEE: 'Employee',
};

export function roleLabel(role: string | null | undefined): string {
  return role ? ((ROLE_LABELS as Partial<Record<string, string>>)[role] ?? role) : '';
}

/** What each role can do — shown under the role picker. */
export const ROLE_HELP: Record<UserRole, string> = {
  EMPLOYEE: 'Own attendance, leave, claims and payslips.',
  MANAGER: 'Also approves leave and regularisation for direct reports.',
  HR: 'Also manages employees, leave and payroll, and approves claims (HR step).',
  ORG_ADMIN: 'Full admin access, including settings and roles. Approves claims (HR step).',
  SUPER_ADMIN: 'Company owner — everything, and the final approval on claims.',
};

// Role management (API rules): only Org Admin / Owner may set a role, only an Owner may
// grant Owner, and nobody can change their own role.
export function canManageRoles(viewer: UserRole | null | undefined): boolean {
  return viewer === 'SUPER_ADMIN' || viewer === 'ORG_ADMIN';
}

export function assignableRoles(viewer: UserRole | null | undefined): UserRole[] {
  const base: UserRole[] = ['EMPLOYEE', 'MANAGER', 'HR', 'ORG_ADMIN'];
  return viewer === 'SUPER_ADMIN' ? [...base, 'SUPER_ADMIN'] : base;
}

/** Why the viewer can't change this employee's role, or null if they can. */
export function roleChangeBlockedReason(
  viewer: UserRole | null | undefined,
  target: UserRole | null | undefined,
  isSelf: boolean,
): string | null {
  if (!canManageRoles(viewer)) return 'Only an Org Admin or the Owner can change roles.';
  if (isSelf) return 'You can’t change your own role.';
  if (target === 'SUPER_ADMIN' && viewer !== 'SUPER_ADMIN')
    return 'Only an Owner can change an Owner’s role.';
  return null;
}

// Mirrors the API's ROLE_RANK: HR and above may edit / delete only people ranked below them;
// the Owner may edit anyone; everyone may edit their own personal details.
export const ROLE_RANK: Record<UserRole, number> = {
  EMPLOYEE: 1,
  MANAGER: 2,
  HR: 3,
  ORG_ADMIN: 4,
  SUPER_ADMIN: 5,
};

export function isHrPlus(role: UserRole | null | undefined): boolean {
  return role === 'HR' || role === 'ORG_ADMIN' || role === 'SUPER_ADMIN';
}

export function canEditEmployee(
  viewer: UserRole | null | undefined,
  target: UserRole | null | undefined,
  isSelf: boolean,
): boolean {
  if (isSelf) return true;
  if (!viewer || !isHrPlus(viewer)) return false;
  if (viewer === 'SUPER_ADMIN') return true;
  return ROLE_RANK[viewer] > ROLE_RANK[target ?? 'EMPLOYEE'];
}

export const ROLE_BADGE_CLASS: Record<UserRole, string> = {
  SUPER_ADMIN:
    'border-transparent bg-brand-orange/15 text-brand-orange-deep dark:text-brand-orange',
  ORG_ADMIN: 'border-transparent bg-action-violet-bg text-action-violet-fg',
  HR: 'border-transparent bg-action-blue-bg text-action-blue-fg',
  MANAGER: 'border-transparent bg-action-green-bg text-action-green-fg',
  EMPLOYEE: 'border-transparent bg-muted text-muted-foreground',
};
